// Flow types automatically removed

/* eslint-disable import/no-default-export */
import { Component } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { ImageUploader } from './ImageUploader';
import { EditPane } from './EditPane';
import { BottomBar } from './BottomBar';
import { numberOfElements, noop } from './utils';
import PenLine from 'dibs-icons/exports/legacy/PenLine';
import ArtLamp from 'dibs-icons/exports/legacy/ArtLamp';
import Framed from 'dibs-icons/exports/legacy/Framed';
import CloseUps from 'dibs-icons/exports/legacy/CloseUps';
import PurseSearch from 'dibs-icons/exports/legacy/PurseSearch';
import PurseHand from 'dibs-icons/exports/legacy/PurseHand';
import PurseReverse from 'dibs-icons/exports/legacy/PurseReverse';
import ChairOutlined from 'dibs-icons/exports/legacy/ChairOutlined';
import ChairSearch from 'dibs-icons/exports/legacy/ChairSearch';
import ChairLamp from 'dibs-icons/exports/legacy/ChairLamp';
import RingSearch from 'dibs-icons/exports/legacy/RingSearch';
import RingHand from 'dibs-icons/exports/legacy/RingHand';
import RingAngled from 'dibs-icons/exports/legacy/RingAngled';
import Plus from 'dibs-icons/exports/legacy/Plus';
import ImageUploadMultiple from 'dibs-icons/exports/legacy/ImageUploadMultiple';
import styles from './styles/MediaUploader.scss';

const handleIf = (shouldHandle, handler) => e => {
    if (shouldHandle) {
        return handler(e);
    }

    return undefined;
};

class MediaUploader extends Component {
    constructor(props) {
        super(props);
        this._onDrop = this._onDrop.bind(this);
        this._onDropReplace = this._onDropReplace.bind(this);
        this._onEditPaneDrop = this._onEditPaneDrop.bind(this);
        this._dragEventsHandler = this._dragEventsHandler.bind(this);
        this._renderThumbnail = this._renderThumbnail.bind(this);
        this._getDragCopy = this._getDragCopy.bind(this);
        this._renderThumbnailPlaceholder = this._renderThumbnailPlaceholder.bind(this);
    }

    _onDrop(index, files, rejectedFiles, mediaId, uploaderId) {
        const { dropRejectedCopy, onDrop, onDropRejected } = this.props;

        if (rejectedFiles.length) {
            return onDropRejected({
                rejectedFiles,
                uploaderId,
                index,
                message: dropRejectedCopy,
            });
        }

        return onDrop({
            files,
            index,
            mediaId,
            uploaderId,
        });
    }

    _onDropReplace(index, files, rejectedFiles, mediaId, uploaderId) {
        const { dropRejectedCopy, onDropReplace, onDropRejected } = this.props;

        if (rejectedFiles.length) {
            return onDropRejected({
                rejectedFiles,
                uploaderId,
                index,
                message: dropRejectedCopy,
            });
        }

        return onDropReplace({
            files,
            index,
            mediaId,
            uploaderId,
        });
    }

    _onEditPaneDrop(index, files, rejectedFiles, mediaId, uploaderId) {
        const { dropRejectedCopy, onEditPaneDrop, onDropRejected } = this.props;

        if (rejectedFiles.length) {
            return onDropRejected({
                rejectedFiles,
                uploaderId,
                index,
                message: dropRejectedCopy,
            });
        }

        if (typeof onEditPaneDrop === 'function') {
            return onEditPaneDrop({
                files,
                mediaId,
                uploaderId,
            });
        }

        return null;
    }

    _dragEventsHandler(e, index) {
        const { draggedItemIndex, isDragging, onDragEnter, onDragChange } = this.props;

        if (e.type === 'dragenter') {
            //avoid dragenters fired by non-mediaList dragged items
            if (isDragging) {
                onDragEnter(draggedItemIndex, index);
                onDragChange(index);
            }
        } else {
            onDragChange(index);
        }
    }

    _renderThumbnail(mediaElement, index) {
        const {
            allowVideo,
            allowImageUploaderReplace,
            draggedItemIndex,
            errors,
            isDragging,
            isPending,
            onDelete,
            onDownload,
            onSelectedIndexChange,
            onRotationChange,
            renderNoMediaPlaceholder,
            requireDeleteConfirmation,
            hoveredImageUploaderIndex,
            dropZoneAccept,
            hideEditPane,
            allowRotation,
            size,
            onImageUploaderDragEnter = noop,
            onImageUploaderDragLeave = noop,
            segmentedView,
            partialSegmentedView,
            selectedIndex,
            excludePrimaryImage,
            openPreviewModal,
            showActionIcons,
        } = this.props;
        const {
            uploaderId,
            mediaId,
            caption,
            uploadPending,
            mediaType,
            mediaLocation,
            isLocked,
            allowDownload,
            error,
        } = mediaElement;
        const hasMedia = !!mediaLocation;
        const dragContainerClasses = classNames(index === draggedItemIndex ? styles.dragged : null);
        const errorMessage = errors[uploaderId] || error || '';
        const mediaClick = hasMedia ? () => onSelectedIndexChange(index) : noop;
        const isPrimary = hasMedia && index === 0 && !excludePrimaryImage;
        const shouldHandleDrag = hasMedia && !isPending;
        const dropZoneAcceptedTypes = dropZoneAccept || 'image/*' + (allowVideo ? ',video/*' : '');
        const onDrop = allowImageUploaderReplace ? this._onDropReplace : this._onDrop;
        return (
            <div
                className={dragContainerClasses}
                draggable
                id={`media-uploader-${index}`} // allows react to swap elements rather than reload each img - DO NOT MODIFY KEY
                key={uploaderId} //onDragStart fired on source element when user starts dragging
                onDragStart={handleIf(shouldHandleDrag, e => this._dragEventsHandler(e, index))}
                onDragEnd={handleIf(shouldHandleDrag, e => this._dragEventsHandler(e, null))} //onDragEnter fired on arbitrary element on mouseEnter while dragging
                //dont reorder when entering empty uploader
                onDragEnter={handleIf(shouldHandleDrag, e => this._dragEventsHandler(e, index))}
            >
                <ImageUploader
                    classes={{
                        dropZone: styles.dropZone,
                        dropZoneSmall: styles.dropZoneSmall,
                        hasImage: styles.hasImage,
                        noImage: styles.noImage,
                        image: styles.image,
                        errorMessage: styles.errorMessage,
                        imageDetailFooter: styles.imageDetailFooter,
                        noPrimaryImage: styles.noPrimaryImage,
                        imageDetailFooterSmall: styles.imageDetailFooterSmall,
                        isDraggable: styles.isDraggable,
                        selectedImage: styles.selectedImage,
                    }}
                    dropZoneAccept={dropZoneAcceptedTypes}
                    errorMessage={errorMessage}
                    hasCaption={!!caption}
                    displayOverlay={
                        allowImageUploaderReplace && index === hoveredImageUploaderIndex
                    }
                    imageUploaderIndex={index}
                    isDraggable={hasMedia}
                    isPending={!!isPending}
                    isPrimary={isPrimary}
                    mediaType={mediaType || 'photo'}
                    mediaId={mediaId}
                    mediaLocation={mediaLocation}
                    uploadPending={uploadPending}
                    renderNoMediaPlaceholder={renderNoMediaPlaceholder}
                    isLocked={isLocked}
                    allowDownload={allowDownload}
                    onImageClick={mediaClick}
                    onDrop={
                        isDragging
                            ? noop
                            : (files, rejectedFiles) =>
                                  onDrop(index, files, rejectedFiles, mediaId, uploaderId)
                    }
                    onDelete={!hasMedia ? noop : e => onDelete(index, e)}
                    onDownload={!hasMedia ? noop : e => onDownload(index, e)}
                    onImageUploaderDragEnter={
                        allowImageUploaderReplace ? onImageUploaderDragEnter : noop
                    }
                    onImageUploaderDragLeave={
                        allowImageUploaderReplace ? onImageUploaderDragLeave : noop
                    }
                    onRotationChange={onRotationChange}
                    requireDeleteConfirmation={requireDeleteConfirmation} // if there is no edit pane, user will rotate using image thumbnails
                    allowRotation={allowRotation && hideEditPane}
                    size={size}
                    segmentedView={segmentedView || partialSegmentedView}
                    isSelected={index === selectedIndex}
                    openPreviewModal={() => openPreviewModal(index)}
                    excludePrimaryImage={excludePrimaryImage}
                    showActionIcons={showActionIcons}
                    showConfirmationBeforeClose
                />
            </div>
        );
    }

    _getDragCopy() {
        const { dragCopy, allowVideo, segmentedView } = this.props;

        if (dragCopy) {
            return dragCopy;
        }

        return (
            <FormattedMessage
                id="dibs-media-uploader.MediaUplaoder.dragCopy"
                defaultMessage="{segmentedView, select,
                    true { or Drag {allowVideo, select, true {Videos and Images} other {Images}} Here}
                    other {Drag {allowVideo, select, true {Videos and Images} other {Images}} Here or }
                }"
                values={{ allowVideo: Boolean(allowVideo), segmentedView: Boolean(segmentedView) }}
            />
        );
    }

    _getBottomBarLocation() {
        const { mediaList, segmentedView, partialSegmentedView, excludePrimaryImage } = this.props;

        if (!(segmentedView || partialSegmentedView) || !mediaList?.length || excludePrimaryImage) {
            return 'bottom';
        }

        return 'belowThumbnails';
    }

    _renderThumbnailPlaceholder(config, index) {
        const { copy, copyClasses, icon, iconClasses, isPrimary } = config;
        const { excludePrimaryImage } = this.props;
        const thumbnailClasses = classNames(
            styles.image,
            styles.imageDetailFooter,
            styles.placeholderThumbnail,
            {
                [styles.noPrimaryImage]: excludePrimaryImage,
            }
        );
        return (
            <div className={thumbnailClasses} key={index}>
                {icon && <div className={classNames(iconClasses, styles.iconWrapper)}>{icon}</div>}
                {copy && (
                    <div className={classNames(copyClasses, styles.placeholderCopy)}>{copy}</div>
                )}
                {isPrimary && <div className={styles.primaryPlaceholderLabel}>Primary Image</div>}
            </div>
        );
    } // There are defined placeholder images that should be overwritten when actual images are uploaded
    // For an item with 0 images, we render 6 placeholders
    // For an item images, there will be at least 8 thumbnails rendered, in rows of 4. Images are shown first, then the rows are completed with placeholders

    _getPlaceholderIndexes() {
        const { mediaList, excludePrimaryImage } = this.props;
        const numImages = mediaList.length;
        const start = numImages - 1;
        let end = 7;

        if (excludePrimaryImage && !numImages) {
            return {
                start: 0,
                end: 8,
            };
        }

        if (!excludePrimaryImage) {
            end = start + (4 - (numImages % 4)); // Make sure there are at least 12 slots (existing images + placeholders), but no more than 20

            end = Math.max(end, 11);
            end = Math.min(end, 19);
            return {
                start,
                end,
            };
        }

        if (excludePrimaryImage) {
            end = start + (4 - (numImages % 4));
            end = Math.max(end, 7);
            end = Math.min(end, 19);
            return {
                start,
                end,
            };
        }

        return {
            start,
            end,
        };
    }

    _getPlaceholders() {
        const { vertical } = this.props;

        if (vertical === 'art') {
            return [
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.art.details"
                            defaultMessage="Details"
                        />
                    ),
                    icon: <CloseUps className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.art.frame"
                            defaultMessage="Frame"
                        />
                    ),
                    icon: <Framed className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.art.inSituation"
                            defaultMessage="In Situation"
                        />
                    ),
                    icon: (
                        <ArtLamp className={classNames(styles.placeholderIcon, styles.largeIcon)} />
                    ),
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.art.signature"
                            defaultMessage="Signature"
                        />
                    ),
                    icon: <PenLine className={styles.placeholderIcon} />,
                },
            ];
        }

        if (vertical === 'fashion') {
            return [
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.fashion.details"
                            defaultMessage="Details"
                        />
                    ),
                    icon: <PurseSearch className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.fashion.onModel"
                            defaultMessage="On Model"
                        />
                    ),
                    icon: <PurseHand className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.fashion.variousAngles"
                            defaultMessage="Various Angles"
                        />
                    ),
                    icon: <PurseReverse className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.fashion.designer"
                            defaultMessage="Designer"
                        />
                    ),
                    icon: <PenLine className={styles.placeholderIcon} />,
                },
            ];
        }

        if (vertical === 'furniture') {
            return [
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.furniture.details"
                            defaultMessage="Details"
                        />
                    ),
                    icon: <ChairSearch className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.furniture.variousAngles"
                            defaultMessage="Various Angles"
                        />
                    ),
                    icon: <ChairOutlined className={styles.placeholderIcon} />,
                    iconClasses: [styles.flipIcon],
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.furniture.inSituation"
                            defaultMessage="In Situation"
                        />
                    ),
                    icon: (
                        <ChairLamp
                            className={classNames(styles.placeholderIcon, styles.largeIcon)}
                        />
                    ),
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.furniture.creator"
                            defaultMessage="Creator"
                        />
                    ),
                    icon: <PenLine className={styles.placeholderIcon} />,
                },
            ];
        }

        if (vertical === 'jewelry') {
            return [
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.jewelry.details"
                            defaultMessage="Details"
                        />
                    ),
                    icon: <RingSearch className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.jewelry.onModel"
                            defaultMessage="On Model"
                        />
                    ),
                    icon: <RingHand className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.jewelry.variousAngles"
                            defaultMessage="Various Angles"
                        />
                    ),
                    icon: <RingAngled className={styles.placeholderIcon} />,
                },
                {
                    copy: (
                        <FormattedMessage
                            id="dibs-media-uploader.MediaUplaoder.jewelry.creator"
                            defaultMessage="Creator"
                        />
                    ),
                    icon: <PenLine className={styles.placeholderIcon} />,
                },
            ];
        }

        return [];
    }

    _renderThumbnailPlaceholders() {
        const { mediaList, excludePrimaryImage } = this.props;
        const primaryImagePlaceholder = {
            copy: (
                <FormattedMessage
                    id="dibs-media-uploader.MediaUplaoder.primaryImagePlaceholder"
                    defaultMessage="This will be the featured image retouched by our team"
                />
            ),
            icon: null,
            isPrimary: true,
            copyClasses: [styles.primaryCopy],
        };

        const placeholders = this._getPlaceholders(); // This will be the PLUS sign thumbail

        const genericPlaceholder = {
            copy: null,
            icon: <Plus className={classNames(styles.placeholderIcon, styles.plusIcon)} />,
            iconClasses: [styles.noCopy],
        }; // This will be the last placeholder shown

        const addMorePlaceholder = {
            copy: (
                <FormattedMessage
                    id="dibs-media-uploader.MediaUplaoder.imageLimit"
                    defaultMessage="Up to 20 Images"
                />
            ),
            icon: <ImageUploadMultiple className={styles.placeholderIcon} />,
        };
        let thumbnailsPlaceholders = [...placeholders, addMorePlaceholder];

        if (!excludePrimaryImage) {
            thumbnailsPlaceholders = [primaryImagePlaceholder, ...placeholders, addMorePlaceholder];
        }

        if (!mediaList.length && !excludePrimaryImage) {
            return thumbnailsPlaceholders.map(this._renderThumbnailPlaceholder);
        }

        const placeholdersToRender = [];

        const { start, end } = this._getPlaceholderIndexes();

        for (let i = start; i < end; i++) {
            if (i === end - 1) {
                placeholdersToRender.push(addMorePlaceholder);
            } else {
                placeholdersToRender.push(placeholders?.[i] || genericPlaceholder);
            }
        }

        return placeholdersToRender.map(this._renderThumbnailPlaceholder);
    }

    render() {
        const {
            allowVideo,
            enableImageRotation,
            allowRotation,
            bottomBarType,
            classes = {},
            dropZoneAccept,
            errors,
            isDragging,
            isPending,
            useButton,
            isRotating,
            maxCaptionChars,
            mediaList,
            pendingCopy,
            requirementsCopy,
            rotation,
            selectedIndex,
            showCaption,
            editPaneLocked,
            lockCopy,
            allowEditPaneDownload,
            onCaptionChange,
            onDownload,
            onRotationChange,
            hideUploadBar,
            hideEditPane,
            enableSelectedImgReplace,
            editPaneOverlayVisible,
            onEditPaneDragEnter = noop,
            onEditPaneDragLeave = noop,
            horizontalView,
            size,
            onDelete,
            requireDeleteConfirmation,
            segmentedView,
            partialSegmentedView,
            onEdit,
            allowEdit,
            editField,
            excludePrimaryImage,
            disabled,
        } = this.props;
        const numUploaders = mediaList.length - 1;
        const numElements = numberOfElements(mediaList);
        const lastElementIndex = Math.max(Math.min(numUploaders, numElements), 0);
        const hasElements = numElements > 0;
        const editPaneMedia = mediaList.length > selectedIndex ? mediaList[selectedIndex] : null;
        const bottomBarMedia =
            mediaList.length > lastElementIndex ? mediaList[lastElementIndex] : null;
        const {
            mediaId: bottomBarMediaId,
            uploaderId: bottomBarUploaderId,
            error: bottomBarError,
        } = bottomBarMedia || {};
        const dropZoneAcceptedTypes = dropZoneAccept || 'image/*' + (allowVideo ? ',video/*' : '');

        const bottomBarLocation = this._getBottomBarLocation();

        let browseCopy = (
            <FormattedMessage
                id="dibs-media-uploader.MediaUploader.browse"
                defaultMessage="Browse"
            />
        );

        if (excludePrimaryImage) {
            browseCopy = (
                <FormattedMessage
                    id="dibs-media-uploader.MediaUploader.uploadAdditionalImages"
                    defaultMessage="Upload Additional Images"
                />
            );
        } else if (segmentedView) {
            browseCopy = (
                <FormattedMessage
                    id="dibs-media-uploader.MediaUploader.uploadImages"
                    defaultMessage="Upload Images"
                />
            );
        }

        const bottomBar = (
            <BottomBar
                allowVideo={allowVideo}
                bottomBarType={bottomBarType}
                browseCopy={browseCopy}
                dragCopy={this._getDragCopy()}
                showBrowseCopyFirst={!!segmentedView}
                dropZoneAccept={dropZoneAcceptedTypes}
                errors={{
                    uploaderError: errors[bottomBarUploaderId] || bottomBarError,
                    generalError: errors.general,
                    standardErrors: errors.errors,
                }}
                isPending={!!isPending}
                mediaListEmpty={!hasElements}
                pendingCopy={pendingCopy}
                requirementsCopy={segmentedView ? null : requirementsCopy}
                useButton={useButton}
                onDrop={(files, rejectedFiles) =>
                    this._onDrop(
                        lastElementIndex,
                        files,
                        rejectedFiles,
                        bottomBarMediaId,
                        bottomBarUploaderId
                    )
                }
                hideUploadBar={hideUploadBar}
                horizontalView={horizontalView}
                segmentedView={segmentedView}
                size={size}
                excludePrimaryImage={excludePrimaryImage}
                disabled={disabled}
                onDropZoneOpen={this.props.onDropZoneOpen}
            />
        );
        return (
            <div
                className={classNames(
                    {
                        [styles.disabledCursor]: isPending,
                        [styles.directionRow]:
                            (horizontalView || segmentedView) && !excludePrimaryImage,
                        [styles.noPrimaryImage]: excludePrimaryImage,
                    },
                    styles.container,
                    classes.container
                )}
            >
                {hasElements && (
                    <div
                        className={classNames({
                            // move images to right of dropzone for horizontal view
                            [styles.horizontalRightSection]: horizontalView,
                            [styles.noPrimaryImage]: excludePrimaryImage,
                        })}
                    >
                        {!horizontalView && (
                            <div className={styles.imageSelectorIndex}>
                                <FormattedMessage
                                    id="dibs-media-uploader.MediaUplaoder.imageSelectorIndex"
                                    defaultMessage="{selectedIndex} of {numElements}"
                                    values={{ selectedIndex: selectedIndex + 1, numElements }}
                                />
                            </div>
                        )}
                        <div className={styles.mediaListContainer}>
                            {!hideEditPane && (
                                <div className={styles.editPaneWrapper}>
                                    <EditPane
                                        allowRotation={allowRotation}
                                        enableImageRotation={enableImageRotation}
                                        dropZoneAccept={dropZoneAcceptedTypes}
                                        isPending={!!isPending}
                                        isRotating={isRotating}
                                        maxCaptionChars={maxCaptionChars}
                                        media={editPaneMedia}
                                        rotation={rotation}
                                        showCaption={showCaption}
                                        isLocked={editPaneLocked}
                                        lockCopy={lockCopy}
                                        allowEdit={allowEdit}
                                        allowDownload={allowEditPaneDownload}
                                        onCaptionChange={onCaptionChange}
                                        onRotationChange={onRotationChange}
                                        onEdit={onEdit}
                                        selectedIndex={selectedIndex}
                                        onDelete={e => onDelete(selectedIndex, e)}
                                        onDownload={
                                            !editPaneMedia
                                                ? noop
                                                : e => onDownload(selectedIndex, e)
                                        }
                                        enableReplace={enableSelectedImgReplace}
                                        overlayVisible={editPaneOverlayVisible}
                                        onDrop={
                                            isDragging || !enableSelectedImgReplace
                                                ? noop
                                                : (files, rejectedFiles) => {
                                                      this._onEditPaneDrop(
                                                          0,
                                                          files,
                                                          rejectedFiles,
                                                          bottomBarMediaId,
                                                          bottomBarUploaderId
                                                      );
                                                  }
                                        }
                                        onDragEnter={
                                            !enableSelectedImgReplace ? noop : onEditPaneDragEnter
                                        }
                                        onDragLeave={
                                            !enableSelectedImgReplace ? noop : onEditPaneDragLeave
                                        }
                                        requireDeleteConfirmation={requireDeleteConfirmation}
                                        segmentedView={segmentedView || partialSegmentedView}
                                        showDeleteButton={segmentedView || partialSegmentedView}
                                        showButtonText={partialSegmentedView}
                                        editField={editField}
                                    />
                                    <div className={styles.bottomBarBelowEditPane}>
                                        {bottomBarLocation === 'belowThumbnails' ? bottomBar : null}
                                    </div>
                                </div>
                            )}
                            <div
                                className={classNames(
                                    styles.mediaUploadersContainer,
                                    styles.noCopy,
                                    {
                                        [styles.segmentedMediaUploadersContainer]: segmentedView,
                                        [styles.noPrimaryImage]: excludePrimaryImage,
                                    }
                                )}
                            >
                                {mediaList.map(this._renderThumbnail)}
                                {segmentedView && this._renderThumbnailPlaceholders()}
                                <div className={styles.flexBreak} />
                                <div className={styles.bottomBarBelowThumbnails}>
                                    {bottomBarLocation === 'belowThumbnails' ? bottomBar : null}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div
                    className={classNames({
                        // move images to right of dropzone for horizontal view
                        [styles.horizontalLeftSection]: horizontalView,
                        [styles.segmentedLeftSection]: segmentedView && !mediaList.length,
                        [styles.noPrimaryImage]: excludePrimaryImage,
                    })}
                >
                    {bottomBarLocation === 'bottom' || (!hasElements && partialSegmentedView)
                        ? bottomBar
                        : null}
                </div>

                <div
                    className={classNames(styles.segmentedPlaceholders, styles.images, {
                        [styles.withCopy]: !mediaList.length && !excludePrimaryImage,
                        [styles.noPrimaryImage]: excludePrimaryImage,
                    })}
                >
                    {segmentedView && !mediaList.length && this._renderThumbnailPlaceholders()}
                </div>
                {!excludePrimaryImage && segmentedView && !mediaList.length && (
                    <div className={styles.copySection}>
                        <p>
                            <FormattedMessage
                                id="dibs-media-uploader.MediaUploader.uploadImages.tip.1"
                                defaultMessage="Images should communicate the attributes you would want buyers to experience in person. Keep the following in mind:"
                            />
                        </p>
                        <ol className={styles.copySectionList}>
                            <li>
                                <FormattedMessage
                                    id="dibs-media-uploader.MediaUploader.uploadImages.tip.2"
                                    defaultMessage="The first photo is edited with a white background and will be used as the main image on the site."
                                />
                            </li>
                            <li>
                                <FormattedMessage
                                    id="dibs-media-uploader.MediaUploader.uploadImages.tip.3"
                                    defaultMessage="Add at least six detail shots, including material, hardware, construction, and creator marks."
                                />
                            </li>
                            <li>
                                <FormattedMessage
                                    id="dibs-media-uploader.MediaUploader.uploadImages.tip.4"
                                    defaultMessage="Do not include additional information, such as text or logos, on any images."
                                />
                            </li>
                        </ol>
                    </div>
                )}
            </div>
        );
    }
}

MediaUploader.defaultProps = {
    dropRejectedCopy: 'Invalid file format',
    errors: {},
    mediaList: [],
    pendingCopy: 'Please wait...',
    rotation: 0,
    selectedIndex: 0,
    hideEditPane: false,
    editPaneLocked: false,
    allowEditPaneDownload: false,
    requireDeleteConfirmation: false,
    useButton: false,
    horizontalView: false,
    size: 'default',
    onDropZoneOpen: noop,
};
export { MediaUploader };
